import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import AppLayout from "../components/AppLayout";
import { ListChecks, CalendarRange, X, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const API_URL = process.env.REACT_APP_API_URL;
const STORAGE_KEY = "worklist_filters";

const todayStr = () => new Date().toISOString().slice(0, 10);

const inputCls =
  "w-full border border-brand-200 rounded-xl px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all bg-brand-50";

const selectCls =
  "w-full border border-brand-200 rounded-xl px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all bg-brand-50";

const StatusBadge = ({ row }) => {
  if (!row.contestado && !row.validado)
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
        Sin resultados
      </span>
    );
  if (row.contestado && !row.validado)
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
        Sin validar
      </span>
    );
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
      Validado
    </span>
  );
};

const WorklistPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [grupos, setGrupos] = useState([]);
  const [examenes, setExamenes] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [filters, setFilters] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
      return {
        startDate: saved.startDate || todayStr(),
        endDate: saved.endDate || todayStr(),
        gruexa_cod: saved.gruexa_cod || "",
        examen_cod: saved.examen_cod || "",
        tipo: saved.tipo || "sin_resultados",
      };
    } catch {
      return {
        startDate: todayStr(),
        endDate: todayStr(),
        gruexa_cod: "",
        examen_cod: "",
        tipo: "sin_resultados",
      };
    }
  });

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get(`${API_URL}/worklist/grupos`, { headers })
      .then((r) => setGrupos(r.data))
      .catch(() => {});
    axios
      .get(`${API_URL}/worklist/examenes`, { headers })
      .then((r) => setExamenes(r.data))
      .catch(() => {});
  }, [token]);

  const fetchWorklist = useCallback(
    async (f) => {
      const active = f || filters;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(active));
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/worklist`, {
          params: {
            startDate: active.startDate,
            endDate: active.endDate,
            gruexa_cod: active.gruexa_cod,
            examen_cod: active.examen_cod,
            tipo: active.tipo,
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        setRows(Array.isArray(data) ? data : []);
        setHasSearched(true);
      } catch {
        toast.error("No se pudo cargar la lista de trabajo");
      } finally {
        setLoading(false);
      }
    },
    [filters, token],
  );

  useEffect(() => {
    fetchWorklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const clearFilters = () => {
    const f = {
      startDate: todayStr(),
      endDate: todayStr(),
      gruexa_cod: "",
      examen_cod: "",
      tipo: "sin_resultados",
    };
    setFilters(f);
    fetchWorklist(f);
  };

  const openDetalle = (paciente_cod, examen_cod) => {
    navigate(
      `/resultado-detalle?paciente_cod=${paciente_cod}&examen_cod=${examen_cod}`,
    );
  };

  const tipoLabel =
    filters.tipo === "sin_validar" ? "Sin validar" : "Sin resultados";

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* Cabecera */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-100 rounded-xl">
            <ListChecks className="h-5 w-5 text-brand-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              Lista de trabajo
            </h1>
            <p className="text-sm text-gray-500">
              Exámenes pendientes por responder o validar
            </p>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-4 space-y-4">
          {/* Selector tipo */}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-2">
              Tipo de lista
            </p>
            <div className="inline-flex rounded-xl border border-brand-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setFilter("tipo", "sin_resultados")}
                className={`px-4 py-2 text-sm font-medium transition-all ${
                  filters.tipo === "sin_resultados"
                    ? "bg-red-500 text-white"
                    : "bg-white text-gray-600 hover:bg-brand-50"
                }`}
              >
                Sin resultados
              </button>
              <button
                type="button"
                onClick={() => setFilter("tipo", "sin_validar")}
                className={`px-4 py-2 text-sm font-medium transition-all border-l border-brand-200 ${
                  filters.tipo === "sin_validar"
                    ? "bg-amber-500 text-white"
                    : "bg-white text-gray-600 hover:bg-brand-50"
                }`}
              >
                Sin validar
              </button>
            </div>
          </div>

          {/* Campos de filtro */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fecha inicio
              </label>
              <input
                type="date"
                className={inputCls}
                value={filters.startDate}
                onChange={(e) => setFilter("startDate", e.target.value)}
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fecha fin
              </label>
              <input
                type="date"
                className={inputCls}
                value={filters.endDate}
                onChange={(e) => setFilter("endDate", e.target.value)}
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Sección
              </label>
              <select
                className={selectCls}
                value={filters.gruexa_cod}
                onChange={(e) => setFilter("gruexa_cod", e.target.value)}
              >
                <option value="">Todas las secciones</option>
                {grupos.map((g) => (
                  <option key={g.gruexa_cod} value={g.gruexa_cod}>
                    {g.gruexa_cod}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Examen
              </label>
              <select
                className={selectCls}
                value={filters.examen_cod}
                onChange={(e) => setFilter("examen_cod", e.target.value)}
              >
                <option value="">Todos los exámenes</option>
                {examenes.map((e) => (
                  <option key={e.examen_cod} value={e.examen_cod}>
                    {e.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fetchWorklist()}
                disabled={loading}
                className="flex items-center gap-1.5 px-5 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-xl transition-all"
              >
                <ListChecks className="h-3.5 w-3.5" />
                Generar
              </button>
              <button
                type="button"
                onClick={clearFilters}
                className="flex items-center gap-1.5 px-4 py-2 border border-brand-200 rounded-xl text-sm text-brand-600 bg-white hover:bg-brand-50 transition-all"
              >
                <X className="h-3.5 w-3.5" />
                Limpiar
              </button>
            </div>
          </div>

          {/* Resumen */}
          {hasSearched && (
            <div className="flex items-center gap-2 flex-wrap">
              <CalendarRange className="h-3.5 w-3.5 text-brand-400" />
              <span className="text-xs text-brand-600 bg-brand-50 border border-brand-200 rounded-full px-3 py-0.5">
                {filters.startDate} → {filters.endDate}
              </span>
              <span
                className={`text-xs font-medium px-3 py-0.5 rounded-full border ${
                  filters.tipo === "sin_validar"
                    ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-red-50 text-red-700 border-red-200"
                }`}
              >
                {tipoLabel}
              </span>
              <span className="text-xs text-gray-400">
                {rows.length} examen(es) pendiente(s)
              </span>
            </div>
          )}
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-brand-gradient text-white">
                  {[
                    "Orden",
                    "Fecha",
                    "Convenio",
                    "Paciente",
                    "Sección",
                    "Cód. examen",
                    "Examen",
                    "Estado",
                    "Acción",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-50">
                {loading ? (
                  <tr>
                    <td colSpan="10" className="py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <div className="h-8 w-8 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm">Generando lista…</span>
                      </div>
                    </td>
                  </tr>
                ) : rows.length > 0 ? (
                  rows.map((row, i) => (
                    <tr
                      key={`${row.paciente_cod}-${row.examen_cod}-${i}`}
                      className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}
                    >
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/order-exams?paciente_cod=${row.paciente_cod}`,
                            )
                          }
                          className="font-mono text-xs bg-brand-100 text-brand-700 hover:bg-brand-200 px-2 py-0.5 rounded font-semibold transition-colors"
                        >
                          {row.paciente_cod}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {row.fecha ? String(row.fecha).slice(0, 10) : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {row.convenio || "—"}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {row.paciente || "—"}
                        {row.propietario && (
                          <p>
                            <span className="text-xs text-gray-400">
                              Propietario:
                            </span>{" "}
                            {row.propietario}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {row.seccion}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded font-semibold">
                          {row.examen_cod}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.examen_nombre || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge row={row} />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            openDetalle(row.paciente_cod, row.examen_cod)
                          }
                          className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-800 text-xs font-medium transition-colors hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Abrir
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="py-14 text-center">
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <ListChecks className="h-10 w-10 text-brand-200" />
                        <span className="text-sm">
                          {hasSearched
                            ? "No hay exámenes pendientes para los filtros seleccionados."
                            : "Selecciona los filtros y pulsa Generar para ver la lista de trabajo."}
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default WorklistPage;
